export type FinancialRouteId =
  | 'overview'
  | 'students'
  | 'fees'
  | 'payments'
  | 'refunds'
  | 'transport'
  | 'boarding'
  | 'cafeteria'
  | 'uniforms'
  | 'invoices'
  | 'fee-structures'
  | 'credits'
  | 'accounts'
  | 'journals'
  | 'periods'
  | 'payables'
  | 'banking'
  | 'budgets'
  | 'grants'
  | 'payroll'
  | 'trial-balance'
  | 'account-statement'
  | 'financial-statements'
  | 'reports'

export interface FinancialRouteConfigItem {
  id: FinancialRouteId
  path: string
  label: string
  parent: string
  group: 'overview' | 'collections' | 'service-charges' | 'accounting' | 'planning'
}

export type FinancialRouteGroup = FinancialRouteConfigItem['group']

export const FINANCIAL_ROUTE_CONFIG: FinancialRouteConfigItem[] = [
  { id: 'overview', path: '/financial', label: 'Overview', parent: '/financial', group: 'overview' },
  { id: 'students', path: '/financial/students', label: 'Students', parent: '/financial', group: 'collections' },
  { id: 'fees', path: '/financial/fees', label: 'Fees', parent: '/financial', group: 'collections' },
  { id: 'fee-structures', path: '/financial/fee-structures', label: 'Fee Structures', parent: '/financial', group: 'collections' },
  { id: 'payments', path: '/financial/payments', label: 'Payments', parent: '/financial', group: 'collections' },
  { id: 'refunds', path: '/financial/refunds', label: 'Refunds', parent: '/financial', group: 'collections' },
  { id: 'invoices', path: '/financial/invoices', label: 'Invoices', parent: '/financial', group: 'collections' },
  { id: 'credits', path: '/financial/credit-notes', label: 'Credit Notes', parent: '/financial', group: 'collections' },
  { id: 'transport', path: '/financial/transport', label: 'Transport Fees', parent: '/financial', group: 'service-charges' },
  { id: 'boarding', path: '/financial/boarding', label: 'Boarding Fees', parent: '/financial', group: 'service-charges' },
  { id: 'cafeteria', path: '/financial/cafeteria', label: 'Cafeteria Orders', parent: '/financial', group: 'service-charges' },
  { id: 'uniforms', path: '/financial/uniforms', label: 'Uniform Orders', parent: '/financial', group: 'service-charges' },
  { id: 'accounts', path: '/financial/accounts', label: 'Chart of Accounts', parent: '/financial', group: 'accounting' },
  { id: 'journals', path: '/financial/journals', label: 'Journals', parent: '/financial', group: 'accounting' },
  { id: 'periods', path: '/financial/periods', label: 'Periods', parent: '/financial', group: 'accounting' },
  { id: 'payables', path: '/financial/payables', label: 'Payables', parent: '/financial', group: 'accounting' },
  { id: 'banking', path: '/financial/banking', label: 'Banking', parent: '/financial', group: 'accounting' },
  { id: 'payroll', path: '/financial/payroll', label: 'Payroll', parent: '/financial', group: 'accounting' },
  { id: 'budgets', path: '/financial/budgets', label: 'Budgets', parent: '/financial', group: 'planning' },
  { id: 'grants', path: '/financial/grants', label: 'Grants', parent: '/financial', group: 'planning' },
  { id: 'trial-balance', path: '/financial/trial-balance', label: 'Trial Balance', parent: '/financial', group: 'planning' },
  { id: 'account-statement', path: '/financial/account-statement', label: 'Account Statement', parent: '/financial', group: 'planning' },
  { id: 'financial-statements', path: '/financial/financial-statements', label: 'Financial Statements', parent: '/financial', group: 'planning' },
  { id: 'reports', path: '/financial/statements', label: 'Management Reports', parent: '/financial', group: 'planning' },
]

// Shared by the sidebar and the in-page section nav so both group the finance
// pages the same way.
export const FINANCIAL_GROUP_LABELS: Record<FinancialRouteConfigItem['group'], string> = {
  overview: 'Overview',
  collections: 'Revenue & Collections',
  'service-charges': 'Service Charges',
  accounting: 'Accounting',
  planning: 'Planning & Reports',
}

export const FINANCIAL_GROUP_ORDER = ['collections', 'service-charges', 'accounting', 'planning'] as const

export const FINANCIAL_ROUTE_MAP = new Map(FINANCIAL_ROUTE_CONFIG.map((route) => [route.path, route]))

export const getFinancialRouteId = (pathname: string): FinancialRouteId => {
  const normalized = pathname === '/financial' ? '/financial' : pathname.replace(/\/+$/, '')
  const exact = FINANCIAL_ROUTE_MAP.get(normalized)
  if (exact) return exact.id

  // Longest matching prefix, so a nested path such as
  // /financial/trial-balance/12 stays on the trial balance rather than falling
  // back to the overview. Anchoring on `${path}/` stops /financial matching
  // everything, which it previously did.
  const nested = FINANCIAL_ROUTE_CONFIG
    .filter((route) => route.path !== '/financial' && normalized.startsWith(`${route.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]

  return nested?.id ?? 'overview'
}

export const getFinancialGroup = (pathname: string): FinancialRouteConfigItem['group'] => {
  const routeId = getFinancialRouteId(pathname)
  return FINANCIAL_ROUTE_CONFIG.find((route) => route.id === routeId)?.group ?? 'collections'
}
